"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.EventService = void 0;
var _centroid = _interopRequireDefault(require("@turf/centroid"));
var _moment = _interopRequireDefault(require("moment"));
var _environment = require("../environment");
var _models = require("../models");
var _app = require("./app");
var _geogroup = require("./geogroup");
var _http = require("./http");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
class EventService {
  static async getAvailableEvents({
    partner,
    geogroupId,
    userLocation,
    page,
    pageSize,
    state,
    automaticSubscription
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (state) state.forEach(value => params.push({
        key: 'state[]',
        value
      }));
      if (automaticSubscription) params.push({
        key: 'automatic_subscription',
        value: automaticSubscription
      });
      if (userLocation) {
        params.push({
          key: 'user_lat',
          value: userLocation.coordinates[1]
        }, {
          key: 'user_long',
          value: userLocation.coordinates[0]
        });
      }
      const {
        results
      } = await _http.HttpService.get('v2', `/geogroups/${geogroupId}/available_events`, params, [], null, partner ? {
        partner: partner.code
      } : {});
      return results.map(data => parseEvent(data, _app.AppService.environment.backendUrl));
    } catch (err) {
      console.error('[EventService][getAvailableEvents]', err);
      throw err;
    }
  }
  static async getEvents({
    period,
    search,
    isPublic,
    ordering,
    page,
    pageSize,
    query
  }) {
    try {
      const params = [{
        key: 'state[]',
        value: period === 'current' ? 'running' : period === 'future' ? 'future' : 'finished'
      }, {
        key: 'ordering',
        value: ordering
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'query',
        value: query
      }];
      if (search) params.push({
        key: 'search',
        value: search
      });
      if (isPublic !== undefined) params.push({
        key: 'public',
        value: isPublic
      });
      const {
        count,
        results
      } = await _http.HttpService.get('v3', `/events`, params);
      const events = results.map(data => parseEvent(data, _app.AppService.environment.backendUrl));
      return {
        count,
        events
      };
    } catch (err) {
      console.error('[EventService][getEvents]', err);
      throw err;
    }
  }
  static async getEvent(id) {
    try {
      const data = await _http.HttpService.get('v3', `/events/${id}`);
      return parseEvent(data, _app.AppService.environment.backendUrl);
    } catch (err) {
      console.error('[EventService][getEvent]', err);
      throw err;
    }
  }
  static async getRanking(eventId, {
    page,
    pageSize,
    groupsIds,
    groupTypes,
    size,
    cyclabilityZoneId,
    search,
    parentId,
    returnedGroupsMode
  }) {
    try {
      const queryParams = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'returned_groups_mode',
        value: returnedGroupsMode === 'excludeSubgroups' ? 'EXCLUDE_SUBGROUP' : returnedGroupsMode === 'onlySites' ? 'ONLY_SUB_GROUP_SITE' : returnedGroupsMode === 'sitesOrCompaniesWithoutSites' ? 'SITE_SUB_GROUP_OR_PARENT_WITHOUT_SITE' : returnedGroupsMode === 'groupsAndSites' ? 'GROUP_AND_SUB_GROUP_SITE' : returnedGroupsMode === 'aggregatedGroupsAndSites' ? 'AGGREGATED_SUB_GROUP_SITE_OR_PARENT_WITHOUT_SITE' : 'ALL_GROUP_LINKED_TO_CHALLENGE'
      }];
      if (groupsIds && groupsIds.length > 0) queryParams.push(...groupsIds.map(id => ({
        key: 'group_ids[]',
        value: id
      })));
      if (groupTypes.length > 0) queryParams.push({
        key: 'geogroup_type',
        value: groupTypes.map(key => _geogroup.typesMap[key]).join(',')
      });
      if (size?.min) queryParams.push({
        key: 'size_range_min',
        value: size.min
      });
      if (size?.max) queryParams.push({
        key: 'size_range_max',
        value: size.max
      });
      if (cyclabilityZoneId) queryParams.push({
        key: 'in_cyclability_zone',
        value: cyclabilityZoneId
      });
      if (search) queryParams.push({
        key: 'search',
        value: search
      });
      if (parentId) queryParams.push({
        key: 'parent_id',
        value: parentId
      });
      const {
        count,
        results
      } = await _http.HttpService.get('v4', `/events/${eventId}/ranking`, queryParams);
      return {
        count,
        items: results.map(data => parseEventRankingItem(data, _app.AppService.environment.backendUrl))
      };
    } catch (err) {
      console.error('[EventService][getRanking]', err);
      throw err;
    }
  }
  static async getIndividualRanking(eventId, {
    page,
    pageSize,
    ...props
  }) {
    try {
      const queryParams = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if ('search' in props && props.search) queryParams.push({
        key: 'search',
        value: props.search
      });
      if ('getConnectedUserRank' in props && props.getConnectedUserRank) queryParams.push({
        key: 'connected_user_rank',
        value: true
      });
      const {
        count,
        results
      } = await _http.HttpService.get('v3', `/events/${eventId}/user_ranking`, queryParams);
      return {
        count,
        items: results.map(data => parseEventIndividualRankingItem(data, _app.AppService.environment.backendUrl))
      };
    } catch (err) {
      console.error('[EventService][getIndividualRanking]', err);
      throw err;
    }
  }
  static async getCyclabilityZones(eventId) {
    try {
      const res = await _http.HttpService.get('v2', `/events/${eventId}/cyclability_zones`);
      return res.map(({
        region_id: regionId,
        epcis
      }) => ({
        regionId,
        children: epcis.map(({
          epci_id: id,
          epci_name: name
        }) => ({
          id,
          name
        }))
      }));
    } catch (err) {
      console.error('[EventService][getCyclabilityZones]', err);
      throw err;
    }
  }
  static async addGeogroup(eventId, {
    type,
    name,
    min,
    max,
    address,
    sameLocation,
    cyclabilityZone,
    icon,
    email
  }) {
    try {
      let data = {
        type: _geogroup.typesMap[type],
        title: name,
        code: name.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '_'),
        size_range: {
          min: min || 0,
          max: max || 100_000_000
        },
        admin: {
          email
        }
      };
      if (address) data = {
        ...data,
        geo_point: address.point,
        address: address.address
      };else if (cyclabilityZone) {
        const {
          geometry
        } = cyclabilityZone;
        if (!geometry) throw new Error('cyclability zone has no geometry');
        data = {
          ...data,
          geo_point: (0, _centroid.default)(geometry).geometry,
          cyclability_zone: cyclabilityZone.id
        };
      }
      if (sameLocation !== undefined) data.is_local = sameLocation;
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      if (icon) formData.append('photo', icon);
      const props = await _http.HttpService.post('v2', `/events/${eventId}/geogroups`, [], [], formData);
      return (0, _geogroup.parseGeogroup)(props, _app.AppService.environment.backendUrl);
    } catch (err) {
      console.error('[EventService][addGeogroup]', err);
      throw err;
    }
  }
  static async getRegistrableGeogroups(userId, eventId) {
    try {
      const res = await _http.HttpService.get('v5', `/users/${userId}/events/${eventId}/can_participate_groups`);
      return res.map(data => (0, _geogroup.parseGeogroup)(data, _app.AppService.environment.backendUrl));
    } catch (err) {
      console.error('[EventService][getRegistrableGeogroups]', err);
      throw err;
    }
  }
}
exports.EventService = EventService;
function parseEvent({
  id,
  title,
  start_datetime,
  end_datetime,
  challenge_template,
  scope_permited_contract_codes,
  scope_permited_group_types,
  scope_permited_sub_group_type,
  scope_permited_group_geo_polygon,
  challenges_count,
  challenges_progress_value,
  public: _public,
  dedicated_website_url,
  sponsor,
  subtitle,
  banner_primary_color,
  banner_secondary_color,
  communication_kit,
  default_participation_group,
  target_levels
}, backendUrl) {
  return new _models.Event(id, title, (0, _moment.default)(start_datetime), (0, _moment.default)(end_datetime), challenge_template ? (0, _geogroup.parseChallengeTemplate)(id, {
    ...challenge_template,
    title,
    start_datetime,
    end_datetime
  }, backendUrl) : null, scope_permited_contract_codes || [], (scope_permited_group_types || []).map(key => _geogroup.backendTypesMap[key]), scope_permited_sub_group_type && _geogroup.subGroupTypesMap[scope_permited_sub_group_type] || null, scope_permited_group_geo_polygon || null, challenges_count || 0, challenges_progress_value || 0, _public ? true : false, dedicated_website_url || null, sponsor || null, subtitle || null, banner_primary_color || null, banner_secondary_color || null, communication_kit || null, default_participation_group || null, target_levels ? target_levels.map(({
    title,
    description,
    picture,
    target: targetValue
  }) => ({
    title,
    picture: `${_environment.defaultEnvironment.backendUrl}${picture}`,
    targetValue,
    description
  })) : null);
}
function parseEventRankingItem({
  id,
  title,
  type,
  photo,
  parent_id: parentId,
  parent_title: parentTitle,
  parent_photo,
  rank,
  leaderboard_index: leaderboardIndex,
  progress_value: progressValue,
  count_participants: participantsCount,
  count_participants_with_not_null_progress,
  size_range,
  challenge_certified: certified
}, backendUrl) {
  return {
    id,
    title,
    type: _geogroup.backendTypesMap[type],
    photo: photo ? `${backendUrl}${photo}` : null,
    parentId,
    parentTitle,
    parentPhoto: parent_photo ? `${backendUrl}${parent_photo}` : null,
    rank,
    leaderboardIndex,
    progressValue,
    participantsCount,
    activeParticipantsCount: count_participants_with_not_null_progress || 0,
    sizeRange: size_range || null,
    certified
  };
}
function parseEventIndividualRankingItem({
  user,
  group,
  rank,
  leaderboard_index: leaderboardIndex,
  progress_value: progressValue
}, backendUrl) {
  return {
    user: {
      id: user.id,
      username: user.username || '',
      profilePicture: user.profile_picture ? `${backendUrl}${user.profile_picture}` : null
    },
    group: {
      id: group.id,
      type: _geogroup.backendTypesMap[group.type],
      title: group.title || '',
      photo: group.photo ? `${backendUrl}${group.photo}` : null,
      sizeRange: group.size_range || null,
      parent: group.parent ? {
        id: group.parent.id,
        title: group.parent.title,
        photo: group.parent.photo,
        sizeRange: group.parent.size_range || null
      } : null
    },
    rank,
    leaderboardIndex,
    progressValue
  };
}